import { useEffect, useState } from "react";
import { toast } from "sonner";
import { createDoctor, updateDoctor, getDepartments, resetDoctorLoginPassword } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";

// 0=Sun..6=Sat, matching moment's .day() (see slot-availability.js on the backend).
const DAYS = [
  { value: 0, label: "Su" },
  { value: 1, label: "Mo" },
  { value: 2, label: "Tu" },
  { value: 3, label: "We" },
  { value: 4, label: "Th" },
  { value: 5, label: "Fr" },
  { value: 6, label: "Sa" },
];
const ALL_DAYS = DAYS.map((d) => d.value);

const EMPTY = {
  name: "",
  department_id: "",
  specialty: "",
  gender: "Male",
  reg_no: "",
  fee: "",
  phone: "",
  password: "",
  shifts: [{ start: "", end: "", days: ALL_DAYS }],
};

// Doubles as the "edit doctor" form — pass a `doctor` prop to prefill and
// switch the submit to an update. There's only one doctor form in the app,
// so this stays a single component rather than a near-duplicate Edit modal.
// The doctor logs in with their phone number + the password set below.
// `onLoginCredentials` surfaces a regenerated password when the "Generate a
// new password" edit-mode action is used instead of typing one (the only way
// to view a working password again once it's been hashed).
export default function AddDoctorModal({ open, onOpenChange, onCreated, doctor, onLoginCredentials }) {
  const [form, setForm] = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resettingLogin, setResettingLogin] = useState(false);
  const editing = !!doctor;

  useEffect(() => {
    if (!open) return;
    setForm(
      doctor
        ? {
            name: doctor.name,
            department_id: doctor.department_id?._id || doctor.department_id || "",
            specialty: doctor.specialty,
            gender: doctor.gender,
            reg_no: doctor.reg_no,
            fee: doctor.fee,
            phone: doctor.phone,
            password: "",
            shifts: doctor.shifts?.length
              ? doctor.shifts.map((s) => ({ start: s.start, end: s.end, days: s.days?.length ? s.days : ALL_DAYS }))
              : [{ start: "", end: "", days: ALL_DAYS }],
          }
        : EMPTY
    );
    getDepartments({ active: true, limit: 100 })
      .then((res) => setDepartments(res.data || []))
      .catch(() => setDepartments([]));
  }, [open, doctor]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setShift = (index, key) => (e) =>
    setForm((f) => ({
      ...f,
      shifts: f.shifts.map((s, i) => (i === index ? { ...s, [key]: e.target.value } : s)),
    }));
  const addShift = () => setForm((f) => ({ ...f, shifts: [...f.shifts, { start: "", end: "", days: ALL_DAYS }] }));
  const removeShift = (index) => setForm((f) => ({ ...f, shifts: f.shifts.filter((_, i) => i !== index) }));
  const toggleShiftDay = (index, day) =>
    setForm((f) => ({
      ...f,
      shifts: f.shifts.map((s, i) =>
        i === index
          ? { ...s, days: s.days.includes(day) ? s.days.filter((d) => d !== day) : [...s.days, day].sort() }
          : s
      ),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.department_id || !form.specialty || !form.reg_no || !form.fee || !form.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!editing && (!form.password || form.password.length < 8)) {
      toast.error("Login password must be at least 8 characters.");
      return;
    }
    if (form.password && form.password.length < 8) {
      toast.error("Login password must be at least 8 characters.");
      return;
    }
    if (form.shifts.some((s) => !!s.start !== !!s.end)) {
      toast.error("Set both a start and end time for each shift, or remove it.");
      return;
    }
    if (form.shifts.some((s) => s.start && s.end && s.days.length === 0)) {
      toast.error("Select at least one day for each shift.");
      return;
    }
    setSaving(true);
    try {
      // Built explicitly, not spread from `form` — `form` was itself
      // prefilled by spreading the full API doctor object (see the effect
      // above), which carries fields like _id/createdAt/delay that the
      // update endpoint's schema rejects as unknown keys.
      const payload = {
        name: form.name,
        department_id: form.department_id,
        specialty: form.specialty,
        gender: form.gender,
        reg_no: form.reg_no,
        fee: Number(form.fee),
        phone: form.phone,
        shifts: form.shifts
          .filter((s) => s.start && s.end)
          .map((s) => ({ start: s.start, end: s.end, days: s.days })),
        ...(form.password ? { password: form.password } : {}),
      };
      if (editing) {
        await updateDoctor(doctor._id, payload);
        toast.success("Doctor updated.");
      } else {
        await createDoctor(payload);
        toast.success("Doctor added.");
      }
      setForm(EMPTY);
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.message || "Could not save doctor.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetLogin = async () => {
    setResettingLogin(true);
    try {
      const res = await resetDoctorLoginPassword(doctor._id);
      onOpenChange(false);
      onLoginCredentials?.({ name: form.name, email: res.data.phone, password: res.data.password });
    } catch (err) {
      toast.error(err?.message || "Could not reset login.");
    } finally {
      setResettingLogin(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit doctor" : "Add doctor"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={set("name")} placeholder="Dr Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.department_id}
              onChange={set("department_id")}
            >
              <option value="">Select department…</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Specialty / designation</Label>
            <Input value={form.specialty} onChange={set("specialty")} placeholder="Senior Consultant" />
          </div>
          <div className="space-y-1.5">
            <Label>Gender</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.gender}
              onChange={set("gender")}
            >
              <option>Male</option>
              <option>Female</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Registration No.</Label>
            <Input value={form.reg_no} onChange={set("reg_no")} placeholder="AMC #1234" />
          </div>
          <div className="space-y-1.5">
            <Label>Consult fee (₹)</Label>
            <Input type="number" value={form.fee} onChange={set("fee")} placeholder="700" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (also their login)</Label>
            <Input value={form.phone} onChange={set("phone")} placeholder="9101234567" />
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Shifts</Label>
            {form.shifts.map((shift, i) => (
              <div key={i} className="space-y-1.5 rounded-md border border-border p-2">
                <div className="flex items-center gap-2">
                  <Input type="time" value={shift.start} onChange={setShift(i, "start")} className="flex-1" />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="time" value={shift.end} onChange={setShift(i, "end")} className="flex-1" />
                  {form.shifts.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeShift(i)}>
                      Remove
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {DAYS.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleShiftDay(i, d.value)}
                      className={cn(
                        "h-7 w-7 rounded-full text-xs font-medium border",
                        shift.days.includes(d.value)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-input"
                      )}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addShift}>
              + Add another shift
            </Button>
            <p className="text-xs text-muted-foreground">
              Leave blank to use the clinic's default hours. Add a shift to limit this doctor's bookable slots to
              their actual working window, and pick which days it applies to — add a second shift for split hours
              on the same day(s), or different hours on different days (e.g. Mon 2–6 PM, Tue 9–11 AM).
            </p>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>{editing ? "New login password (leave blank to keep current)" : "Login password *"}</Label>
            <Input type="password" value={form.password} onChange={set("password")} placeholder="At least 8 characters" />
          </div>

          {editing && (
            <div className="col-span-2 space-y-1">
              <Button type="button" size="sm" variant="link" className="h-auto p-0" disabled={resettingLogin} onClick={handleResetLogin}>
                {resettingLogin ? "Generating…" : "Or generate a random password instead"}
              </Button>
              <p className="text-xs text-muted-foreground">Since passwords are encrypted, this is the only way to view a generated one.</p>
            </div>
          )}

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add doctor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
